// Required template fields left empty, under the project's signing policy
// `placeholders` ('off' | 'warn' | 'block', carried on the workflow read as
// `signing_policy.placeholders`): GET /file/{id}/placeholders/status reads
// them from the saved document. 'warn' tells the signer; 'block' keeps the
// signature from starting until they are filled (the backend refuses too).

import { useEffect, useState } from 'react';
import i18n from '@/i18n';
import baseAxios from '@/shared/utils/request.js';
import { auth } from '@/shared/utils/auth.js';
import { FILE_PLACEHOLDERS_STATUS } from '@/shared/utils/urls.js';
import { field } from './signingState';

const CHECKED = new Set(['warn', 'block']);

/** {mode, missing: [{key, label}]} when the policy checks and something required is empty; else null. */
export function placeholderCheck(mode, status) {
  if (!CHECKED.has(mode) || !status) return null;
  const missing = field(status, 'required_missing');
  if (!Array.isArray(missing) || missing.length === 0) return null;
  return { mode, missing: missing.map((m) => ({ key: m?.key, label: m?.label || m?.key })) };
}

/** The status read, made only while the policy checks; null otherwise or on failure. */
export function usePlaceholderStatus(fileId, mode, guestToken = null) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    setStatus(null);
    if (!fileId || !CHECKED.has(mode)) return undefined;
    let live = true;
    const headers = guestToken ? { Authorization: `Bearer ${guestToken}` } : auth().headers;
    baseAxios({ method: 'GET', url: FILE_PLACEHOLDERS_STATUS.replace('{id}', fileId), headers })
      .then((res) => { if (live) setStatus(res?.data || null); })
      .catch(() => { if (live) setStatus(null); });
    return () => { live = false; };
  }, [fileId, mode, guestToken]);
  return status;
}

/**
 * `errorMessages` for a request the policy may refuse with 409
 * required_placeholders_missing ({missing: [{key, label}]} in its params):
 * the toast names the empty fields.
 */
export const placeholderErrorMessages = {
  required_placeholders_missing: (parsed) => {
    const labels = (parsed?.params?.missing || []).map((m) => m?.label || m?.key).filter(Boolean);
    return labels.length
      ? i18n.t('errors:required_placeholders_missing_fields', { fields: labels.join(', ') })
      : i18n.t('errors:required_placeholders_missing');
  },
};
